// Pure data-derivation helpers for the Requirement Intelligence drawer.
import type { RequirementFinding } from "../../api/contentRegistryApi";
import {
  CONFIDENCE_MODERATE_THRESHOLD,
  CONFIDENCE_STRONG_THRESHOLD,
  MANDATORY_SHARE_THRESHOLD,
  REQUIREMENT_DECOMPOSED_MODERATE_CLS,
  REQUIREMENT_DECOMPOSED_STRONG_CLS,
  REQUIREMENT_DECOMPOSED_WEAK_CLS,
  REQUIREMENT_MODAL_META,
  REQUIREMENT_OTHER_STROKES,
  type RequirementModalMeta,
  type RequirementStrengthKind,
  type RequirementStatusKind,
  type RequirementTabKey,
} from "../../constants";
import { plainText } from "../../utils";

export function titleCase(s: string): string {
  if (!s) return s;
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}

export function metaForModal(modal: string, otherIndex = 0): RequirementModalMeta {
  const key = modal.trim().toLowerCase();
  const known = REQUIREMENT_MODAL_META[key];
  if (known) return known;
  return {
    label: titleCase(key),
    kind: "other",
    dot: "bg-indigo-500 dark:bg-indigo-400",
    stroke: REQUIREMENT_OTHER_STROKES[otherIndex % REQUIREMENT_OTHER_STROKES.length],
  };
}

export function strengthKindFromFinding(
  f: RequirementFinding,
): RequirementStrengthKind {
  const s = (f.strength || "").trim().toLowerCase();
  if (s === "mandatory") return "mandatory";
  if (s === "prohibited") return "prohibited";
  if (s === "permitted") return "permitted";
  const m = (f.modal || "").trim().toLowerCase();
  return metaForModal(m).kind;
}

export function formatConstraints(c: RequirementFinding["constraints"]): string {
  if (!c) return "";
  if (Array.isArray(c)) {
    return c
      .filter(Boolean)
      .map((item) =>
        typeof item === "string" ? plainText(item) : plainText(String(item)),
      )
      .filter(Boolean)
      .join("; ");
  }
  if (typeof c === "string") return plainText(c);
  try {
    return plainText(JSON.stringify(c));
  } catch {
    return "";
  }
}

export function safeCount(v: unknown, fallback = 0): number {
  const n = typeof v === "number" && Number.isFinite(v) ? v : fallback;
  return Math.max(0, Math.floor(n));
}

/* Prefer the backend's own by-modal map; derive from findings when absent. */
export function deriveByModal(
  metricsByModal: Record<string, number> | undefined,
  findings: RequirementFinding[],
): Record<string, number> {
  if (metricsByModal && Object.keys(metricsByModal).length > 0) {
    return metricsByModal;
  }
  const derived: Record<string, number> = {};
  for (const f of findings) {
    const m = (f.modal || "").trim().toLowerCase();
    if (!m) continue;
    derived[m] = (derived[m] ?? 0) + 1;
  }
  return derived;
}

export interface DonutSlice {
  key: string;
  count: number;
  meta: RequirementModalMeta;
}

const KIND_RANK: Record<RequirementStrengthKind, number> = {
  mandatory: 0,
  prohibited: 1,
  permitted: 2,
  other: 3,
};

export function buildDonutSlices(byModal: Record<string, number>): DonutSlice[] {
  const entries = Object.entries(byModal).filter(([, v]) => v > 0);
  let otherIdx = 0;
  const withMeta = entries.map(([key, count]) => {
    const knownKey = key.trim().toLowerCase();
    const known = REQUIREMENT_MODAL_META[knownKey];
    const meta = known ?? metaForModal(knownKey, otherIdx++);
    return { key, count, meta };
  });
  withMeta.sort((a, b) => {
    const kd = KIND_RANK[a.meta.kind] - KIND_RANK[b.meta.kind];
    if (kd !== 0) return kd;
    return b.count - a.count;
  });
  return withMeta;
}

export interface KindCounts {
  mandatory: number;
  prohibited: number;
  permitted: number;
  other: number;
}

export function computeKindCounts(findings: RequirementFinding[]): KindCounts {
  const c: KindCounts = { mandatory: 0, prohibited: 0, permitted: 0, other: 0 };
  for (const f of findings) {
    c[strengthKindFromFinding(f)] += 1;
  }
  return c;
}

export function computeTakeaway(counts: KindCounts): string {
  const kindTotal = counts.mandatory + counts.prohibited + counts.permitted;
  if (kindTotal === 0) return "";
  const mandatoryShare = counts.mandatory / kindTotal;
  const parts: string[] = [];
  if (mandatoryShare > MANDATORY_SHARE_THRESHOLD) {
    parts.push(
      "This document is almost entirely mandatory obligations - a control policy, not guidance.",
    );
  } else {
    parts.push(
      `Mix of ${counts.mandatory} mandatory, ${counts.prohibited} prohibited and ${counts.permitted} permitted obligations.`,
    );
  }
  if (counts.prohibited > 0 && mandatoryShare > MANDATORY_SHARE_THRESHOLD) {
    parts.push(
      `The ${counts.prohibited} prohibitions are the 'shall not' clauses worth reviewing first.`,
    );
  }
  return parts.join(" ");
}

export function resolveStatusKind(
  rawStatus: string | undefined | null,
  notDecomposed: number,
): RequirementStatusKind {
  const s = (rawStatus || "").toLowerCase();
  if (s === "failed") return "failed";
  if (s === "partial" || notDecomposed > 0) return "partial";
  return "complete";
}

export function filterByTab(
  findings: RequirementFinding[],
  tab: RequirementTabKey,
): RequirementFinding[] {
  switch (tab) {
    case "mandatory":
      return findings.filter((f) => strengthKindFromFinding(f) === "mandatory");
    case "prohibited":
      return findings.filter((f) => strengthKindFromFinding(f) === "prohibited");
    case "flagged":
      return findings.filter((f) => f.flagged === true);
    default:
      return findings;
  }
}

export function buildBannerText(
  statusKind: RequirementStatusKind,
  total: number,
  fullyDecomposed: number,
  notDecomposed: number,
): string {
  const base = `Run ${statusKind === "complete" ? "complete" : statusKind}. ${total} normative requirements extracted and ${fullyDecomposed} broken into fields.`;
  if (notDecomposed > 0) {
    return `${base} ${notDecomposed} clauses were not decomposed - the per-run cap was reached, not an extraction failure.`;
  }
  return base;
}

/* Decomposed-percentage → text-tone class. Replaces an inline ternary chain. */
export function decomposedToneClass(decomposedPct: number): string {
  if (decomposedPct >= CONFIDENCE_STRONG_THRESHOLD * 100) {
    return REQUIREMENT_DECOMPOSED_STRONG_CLS;
  }
  if (decomposedPct >= CONFIDENCE_MODERATE_THRESHOLD * 100) {
    return REQUIREMENT_DECOMPOSED_MODERATE_CLS;
  }
  return REQUIREMENT_DECOMPOSED_WEAK_CLS;
}

/* Stable row key for the requirements list, given index within the current page. */
export function requirementRowKey(
  finding: RequirementFinding,
  absoluteIndex: number,
): string {
  const id =
    finding.id || finding.requirement_id || finding.clause_id || "req";
  return `${id}-${absoluteIndex}`;
}
